// apps/web/src/lib/compras/importacao-xml.ts — Fase 13, Plano 22 (COMP-03, D-15, T-13-22B).
// Importação de NF de compra por XML. O XML é lido como Base64 e enviado SEM parse no navegador (o servidor valida, confere o
// destinatário e reserva a chave). Aqui ficam só a leitura do arquivo e a tradução da resposta em mensagem por arquivo.
import type { ResultadoImportacaoNota, SituacaoNotaCompra } from '@pieltec/contracts';
import { rotuloSituacaoNota } from './notas';

export type TipoResultadoImportacao = 'ok' | 'duplicada' | 'invalida' | 'destinatario' | 'erro';

export interface ResultadoArquivo {
  readonly tipo: TipoResultadoImportacao;
  /** Texto pronto para a tela (pt-BR). */
  readonly mensagem: string;
  /** NF criada (tipo ok) ou NF que já existia (tipo duplicada de compra). */
  readonly notaId: string | null;
}

export const MENSAGEM_XML_INVALIDO = 'Não foi possível ler este XML. Use o arquivo XML da NF-e.';
export const MENSAGEM_OUTRO_DESTINATARIO = 'Esta nota não é destinada à Pieltec. Confira o arquivo.';
export const MENSAGEM_FALHA_IMPORTACAO = 'Não foi possível importar este arquivo. Tente novamente.';
export const MENSAGEM_SEM_PERMISSAO_IMPORTAR = 'Você não tem permissão para importar notas de compra.';

const DATA_BR = /(\d{2}\/\d{2}\/\d{4})/;
const DATA_ISO = /^(\d{4})-(\d{2})-(\d{2})/;

/** Lê o arquivo como Base64 puro (bytes do arquivo, sem prefixo `data:`). */
export async function arquivoParaBase64(arquivo: Blob): Promise<string> {
  const bytes = new Uint8Array(await arquivo.arrayBuffer());
  let binario = '';
  const BLOCO = 0x8000;
  for (let inicio = 0; inicio < bytes.length; inicio += BLOCO) {
    binario += String.fromCharCode(...bytes.subarray(inicio, inicio + BLOCO));
  }
  return btoa(binario);
}

function registro(corpo: unknown): Record<string, unknown> {
  return corpo && typeof corpo === 'object' ? (corpo as Record<string, unknown>) : {};
}

function texto(valor: unknown): string | null {
  return typeof valor === 'string' && valor.trim() !== '' ? valor : null;
}

function dataDaDuplicada(dados: Record<string, unknown>): string | null {
  const iso = texto(dados.importadaEm);
  if (iso !== null) {
    const partes = DATA_ISO.exec(iso);
    if (partes) return `${partes[3]}/${partes[2]}/${partes[1]}`;
  }
  const mensagem = texto(dados.message);
  const dataNoTexto = mensagem === null ? null : DATA_BR.exec(mensagem);
  return dataNoTexto ? (dataNoTexto[1] ?? null) : null;
}

/** Traduz a resposta do `POST /compras/notas` (status + corpo já lido) no resultado de UM arquivo. */
export function resultadoDaImportacao(status: number, corpo: unknown): ResultadoArquivo {
  const dados = registro(corpo);
  const codigo = texto(dados.codigo);

  if (status >= 200 && status < 300) {
    const nota = dados as Partial<ResultadoImportacaoNota>;
    const numero = texto(nota.numero) ?? '';
    return {
      tipo: 'ok',
      mensagem: `NF ${numero} importada — ir para a conferência`,
      notaId: texto(nota.notaId),
    };
  }
  if (status === 403) return { tipo: 'erro', mensagem: MENSAGEM_SEM_PERMISSAO_IMPORTAR, notaId: null };
  if (codigo === 'NOTA_JA_IMPORTADA' || status === 409) {
    const data = dataDaDuplicada(dados);
    const notaId = texto(dados.notaId) ?? texto(dados.notaCompraId);
    const situacao = texto(dados.situacao);
    const rotulo = situacao === null ? null : rotuloSituacaoNota(situacao as SituacaoNotaCompra);
    const quando = data === null ? '' : ` em ${data}`;
    const origemCliente = notaId === null && texto(dados.documentoFiscalId) !== null;
    if (origemCliente) return { tipo: 'duplicada', mensagem: `Esta NF já foi importada${quando} como nota de cliente.`, notaId: null };
    return { tipo: 'duplicada', mensagem: `Esta NF já foi importada${quando}${rotulo === null ? '' : ` (${rotulo})`}.`, notaId };
  }
  if (codigo === 'NOTA_OUTRO_DESTINATARIO') return { tipo: 'destinatario', mensagem: MENSAGEM_OUTRO_DESTINATARIO, notaId: null };
  if (codigo === 'XML_INVALIDO') return { tipo: 'invalida', mensagem: MENSAGEM_XML_INVALIDO, notaId: null };
  if (codigo === 'NOTA_EMITENTE_INVALIDO') {
    return { tipo: 'erro', mensagem: texto(dados.message) ?? MENSAGEM_FALHA_IMPORTACAO, notaId: null };
  }
  return { tipo: 'erro', mensagem: MENSAGEM_FALHA_IMPORTACAO, notaId: null };
}

/** Falha de rede/leitura antes de haver resposta do servidor. */
export function resultadoDeFalha(): ResultadoArquivo {
  return { tipo: 'erro', mensagem: MENSAGEM_FALHA_IMPORTACAO, notaId: null };
}

/** "Ir para a conferência" só quando exatamente 1 arquivo foi importado; com vários, o usuário volta à lista. */
export function destinoAposImportacao(resultados: readonly Pick<ResultadoArquivo, 'tipo'>[]): 'conferencia' | 'lista' {
  return resultados.filter((resultado) => resultado.tipo === 'ok').length === 1 ? 'conferencia' : 'lista';
}

/** Rota da conferência de uma NF (a página lê `?id=`). */
export function urlConferencia(notaId: string): string {
  return `/compras/notas/conferencia?id=${encodeURIComponent(notaId)}`;
}
